<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-row dense>
      <!-- Host -->
      <v-col cols="9">
        <v-text-field
          v-model="coAPSettings.host"
          :label="$t('labels.host')"
          prefix="coap://"
          variant="outlined"
          density="compact"
          :rules="[rules.required, rules.validHost]"
          :disabled="!isOwner"
          required
        ></v-text-field>
      </v-col>
      <!-- Port -->
      <v-col cols="3">
        <v-text-field
          v-model="coAPSettings.port"
          :label="$t('labels.port')"
          variant="outlined"
          density="compact"
          :rules="[rules.required, rules.numeric]"
          :disabled="!isOwner"
          required
          type="number"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <!-- Path Name -->
      <v-col cols="12">
        <v-text-field
          v-model="coAPSettings.pathName"
          :label="$t('labels.path-name')"
          variant="outlined"
          density="compact"
          :rules="[rules.required, rules.validPathName]"
          :disabled="!isOwner"
          required
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="6">
        <v-select
          v-model="coAPSettings.method"
          :items="methods"
          :label="$t('labels.method')"
          item-title="text"
          item-value="value"
          variant="outlined"
          density="compact"
          :rules="[rules.required]"
          :disabled="!isOwner"
          required
        ></v-select>
      </v-col>
      <v-col cols="6">
        <v-select
          v-model="coAPSettings.contentFormat"
          :items="contentFormats"
          :label="$t('labels.content-format')"
          item-title="text"
          item-value="value"
          variant="outlined"
          density="compact"
          :disabled="!isOwner"
        ></v-select>
      </v-col>
    </v-row>
    <v-row
      dense
      v-if="coAPSettings.method == 'POST' || coAPSettings.method == 'PUT'"
    >
      <v-col cols="12">
        <v-textarea
          v-model="coAPSettings.payload"
          :label="$t('labels.payload')"
          variant="outlined"
          density="compact"
          :disabled="!isOwner"
        ></v-textarea>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          variant="outlined"
          color="secondary"
          :disabled="!isOwner"
          @click="resetFields"
        >
          {{ $t("labels.reset") }}
        </v-btn>
        <v-btn
          class="ml-4"
          variant="outlined"
          color="green"
          :disabled="!isOwner || !valid"
          @click="testConnection"
        >
          {{ $t("labels.test-connection") }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup>
import {
  ref,
  defineEmits,
  watch,
  useTemplateRef,
  nextTick,
  defineExpose,
} from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineExpose({
  resetForm,
});

const props = defineProps(["bindings", "isOwner"]);

const emit = defineEmits(["dataSourceAssociation", "resetFields"]);

const form = useTemplateRef("form");

const valid = ref(false);
const coAPSettings = ref({
  type: "coap",
  host: "",
  port: "",
  pathName: "",
  method: undefined,
  payload: "",
  contentFormat: "application/json",
});

const coAPSettingsDefault = {
  type: "coap",
  host: "",
  port: "",
  pathName: "",
  method: undefined,
  payload: "",
  contentFormat: "application/json",
};

const methods = [
  {
    text: "POST",
    value: "POST",
  },
  {
    text: "GET",
    value: "GET",
  },
  {
    text: "PUT",
    value: "PUT",
  },
];

const contentFormats = [
  {
    text: "text/plain",
    value: "text/plain",
  },
  {
    text: "text/xml",
    value: "text/xml",
  },
  {
    text: "application/json",
    value: "application/json",
  },
];

const validHostRegex = /^(?!https?:\/\/|coap:\/\/)([a-zA-Z0-9.-]+)(?::\d+)?$/;
const validPathNameRegex = /^\/.*/;
const rules = {
  required: (v) => !!v || t("errors.required-field"),
  numeric: (v) => !isNaN(parseFloat(v)) || t("errors.numeric-field"),
  validHost: (v) => validHostRegex.test(v) || t("errors.invalid-host"),
  validPathName: (v) =>
    validPathNameRegex.test(v) || t("errors.invalid-path-name"),
};

watch(
  () => props.bindings,
  (newVal) => {
    if (newVal) {
      coAPSettings.value = newVal;
    } else {
      resetForm();
    }
  },
  { immediate: true },
);

function resetForm() {
  coAPSettings.value = JSON.parse(JSON.stringify(coAPSettingsDefault));

  nextTick(() => {
    form.value?.resetValidation();
  });
}

function testConnection() {
  let action = {
    ...coAPSettings.value,
  };
  emit("dataSourceAssociation", action);
}

function resetFields() {
  emit("resetFields");
}
</script>
